/**
 * Centralized feature flags (build-time, from REACT_APP_* env).
 *
 * Environment behavior:
 * - REACT_APP_MERCHANT_COMMAND_CENTER_ENABLED  (default ON)
 *     OFF: the legacy landing/tab experience remains available as a rollback.
 *     ON:  the current Merchant Command Center becomes the default home.
 * - REACT_APP_COMPETITIVE_INTEL_ENABLED        (default OFF)
 *     OFF: Competitive Intelligence is REMOVED from the active runtime
 *          (decommissioned) — nav, routes, localhost polling all disabled.
 *     ON:  CI is restored (restoration path; code is preserved, see
 *          docs/POSTPONED_COMPETITIVE_INTELLIGENCE.md).
 * - REACT_APP_MERCHANT_PROFILER_BFF_ENABLED     (default ON)
 *     OFF: legacy Profiler (rollback path).
 *     ON:  BFF-backed Merchant Profiler (all data via the shared API client).
 * - REACT_APP_SEMANTIC_SEARCH_ENABLED           (default OFF)
 *     Optional, non-blocking semantic search capability for the Profiler. Awaits
 *     live cutover verification (see docs/SEMANTIC_SEARCH_CUTOVER.md). When off,
 *     semantic controls are hidden/disabled and NO embedding model loads.
 * - REACT_APP_TURNSTILE_ENABLED                 (default ON)
 *     Temporary Preview-only auth simplification. Production keeps Turnstile
 *     enabled; Preview may set this to false while auth validation runs.
 *
 * Env is read via the CRA-safe helper (see src/shared/env.js) — referencing
 * process.env.<NAME> directly so DefinePlugin inlines it in the browser build.
 */

import { readEnv, readBool } from "./env";

/**
 * Test-only flag override, compiled in ONLY when REACT_APP_E2E === "true" (never
 * set for the real production/Vercel build). Reads window.__NEXUS_E2E_FLAGS__
 * (injected by Playwright before the bundle evaluates). Dead code in production.
 */
const E2E = readEnv("REACT_APP_E2E") === "true";
function resolve(name, envName, def) {
  if (E2E && typeof window !== "undefined") {
    const o = window.__NEXUS_E2E_FLAGS__;
    if (o && Object.prototype.hasOwnProperty.call(o, name)) return !!o[name];
  }
  return readBool(envName, def);
}

export const flags = Object.freeze({
  // The Command Center is now the supported home: it only reads the server
  // access catalog and opens existing, independently authorized workspaces.
  // Keep the env switch as an emergency rollback, but do not make a complete
  // safe product surface disappear by default.
  merchantCommandCenter: resolve("merchantCommandCenter", "REACT_APP_MERCHANT_COMMAND_CENTER_ENABLED", true),
  competitiveIntel: resolve("competitiveIntel", "REACT_APP_COMPETITIVE_INTEL_ENABLED", false),
  // Read-only BFF workspaces are backed by the verified legacy repository
  // composition; mutation-heavy BFF surfaces remain opt-in below.
  merchantProfilerBff: resolve("merchantProfilerBff", "REACT_APP_MERCHANT_PROFILER_BFF_ENABLED", true),
  semanticSearch: resolve("semanticSearch", "REACT_APP_SEMANTIC_SEARCH_ENABLED", false),
  turnstile: resolve("turnstile", "REACT_APP_TURNSTILE_ENABLED", true),
  acquisitionPipelineBff: resolve("acquisitionPipelineBff", "REACT_APP_ACQUISITION_PIPELINE_BFF_ENABLED", false),
  mallsBff: resolve("mallsBff", "REACT_APP_MALLS_BFF_ENABLED", true),
  savedMerchantsBff: resolve("savedMerchantsBff", "REACT_APP_SAVED_MERCHANTS_BFF_ENABLED", false),
});

export function isEnabled(name) {
  return !!flags[name];
}
